import React from 'react'
import { Box } from 'theme-ui'

/**
 * Page markers for the scroll-snap carousel, plus the autoplay pause control.
 *
 * Markers exist because the peek of the next card is not enough on its own.
 * Above the 768px breakpoint the hero lays text left / image right, so a phone
 * in landscape showed 80px of the next card that was entirely blank white - no
 * photo, no headline, nothing identifiable. The peek was measurably present and
 * still conveyed nothing.
 *
 * These are real buttons with a real state, not decoration: they say how many
 * slides there are, which one you are on, and jump to any of them.
 *
 * The pause button is required, not decorative. WCAG 2.2.2 (Pause, Stop, Hide)
 * says auto-advancing content running longer than five seconds needs a mechanism
 * to pause it. Lighthouse does not test for this, so an accessibility score of
 * 100 does not mean it is satisfied - which is exactly how the previous
 * react-slick carousel shipped with a 3s autoplay and no pause control.
 *
 * The browser-native alternatives (::scroll-marker, ::scroll-button) would give
 * the markers for free, but Safari and Firefox do not ship them yet.
 */
const CarouselMarkers = ({
  count,
  active,
  onSelect,
  label,
  autoplay,
  paused,
  onToggleAutoplay,
  labels = {}
}) => {
  const t = {
    group: labels.group || 'Karussell',
    slide: labels.slide || 'Folie',
    pause: labels.pause || 'Pause',
    play: labels.play || 'Abspielen'
  }

  if (count < 2) return null

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        mt: 3,
        /* Phones on their side have ~430 px of height, and the hero card alone
           fills most of it - at the default spacing the markers landed 21 px
           below the fold, so shrink them rather than leave the affordance
           needing a scroll. */
        '@media (max-height: 560px) and (orientation: landscape)': {
          mt: 1
        }
      }}
    >
      {autoplay ? (
        <Box
          as='button'
          type='button'
          onClick={onToggleAutoplay}
          aria-label={paused ? t.play : t.pause}
          aria-pressed={paused}
          sx={{
            appearance: 'none',
            border: 0,
            p: 0,
            width: '44px',
            height: '44px',
            display: 'grid',
            placeItems: 'center',
            background: 'transparent',
            cursor: 'pointer',
            color: 'omega',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'alpha',
              outlineOffset: '-6px',
              borderRadius: '8px'
            },
            '@media (max-height: 560px) and (orientation: landscape)': {
              height: '28px'
            }
          }}
        >
          {/* The icon shows the ACTION the button performs, which is what a
              toggle should convey. aria-pressed carries the state. */}
          {paused ? (
            <svg width='10' height='12' viewBox='0 0 10 12' aria-hidden='true' focusable='false'>
              <path d='M0 0 L10 6 L0 12 Z' fill='currentColor' />
            </svg>
          ) : (
            <svg width='10' height='12' viewBox='0 0 10 12' aria-hidden='true' focusable='false'>
              <rect x='0' y='0' width='3' height='12' fill='currentColor' />
              <rect x='7' y='0' width='3' height='12' fill='currentColor' />
            </svg>
          )}
        </Box>
      ) : null}

      <Box
        role='tablist'
        aria-label={label || t.group}
        sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
      >
        {Array.from({ length: count }, (_, index) => {
          const isActive = index === active
          return (
            <Box
              as='button'
              type='button'
              key={index}
              role='tab'
              aria-selected={isActive}
              aria-label={`${t.slide} ${index + 1} / ${count}`}
              onClick={() => onSelect(index)}
              sx={{
                // 44px hit area, 10px dot: the target is comfortable without the
                // dot looking heavy.
                appearance: 'none',
                border: 0,
                p: 0,
                width: '44px',
                height: '44px',
                '@media (max-height: 560px) and (orientation: landscape)': {
                  height: '28px'
                },
                display: 'grid',
                placeItems: 'center',
                background: 'transparent',
                cursor: 'pointer',
                '&::before': {
                  content: '""',
                  display: 'block',
                  width: isActive ? '22px' : '10px',
                  height: '10px',
                  borderRadius: '999px',
                  bg: isActive ? 'alpha' : 'omega',
                  opacity: isActive ? 1 : 0.45,
                  transition: 'width 200ms ease, opacity 200ms ease'
                },
                '&:hover::before': { opacity: isActive ? 1 : 0.7 },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'alpha',
                  outlineOffset: '-6px',
                  borderRadius: '8px'
                }
              }}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default CarouselMarkers
